import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { fetchProductByHandle, fetchCollectionWithProducts } from '@/services/shopify';
import { useCartStore } from '@/stores/cartStore';
import { ShopBreadcrumb } from '@/components/Shop/ShopBreadcrumb';
import { ShopCategoryNav } from '@/components/Shop/ShopCategoryNav';
import { ProductCard } from '@/components/Shop/ProductCard';
import { ProductImageGallery } from '@/components/Shop/ProductImageGallery';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import { Button } from '@/components/ui/button';
import { Loader2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
const ProductDetail = () => {
  const {
    handle
  } = useParams<{
    handle: string;
  }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const addItem = useCartStore(state => state.addItem);
  useEffect(() => {
    const loadProduct = async () => {
      if (!handle) return;
      setIsLoading(true);
      try {
        const fetchedProduct = await fetchProductByHandle(handle);
        setProduct(fetchedProduct);
        if (fetchedProduct?.variants?.edges?.[0]) {
          setSelectedVariantId(fetchedProduct.variants.edges[0].node.id);
        }

        // Fetch related products from the same collection
        if (fetchedProduct?.collections?.edges?.[0]) {
          const collectionHandle = fetchedProduct.collections.edges[0].node.handle;
          const collectionData = await fetchCollectionWithProducts(collectionHandle, 12);
          if (collectionData) {
            // Filter out the current product and limit to 8 related products
            const related = collectionData.products.edges.filter(p => p.node.id !== fetchedProduct.id).slice(0, 8);
            setRelatedProducts(related);
          }
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error("Failed to load product");
      } finally {
        setIsLoading(false);
      }
    };
    loadProduct();
  }, [handle]);
  const handleAddToCart = () => {
    if (!product || !selectedVariantId) return;
    const selectedVariant = product.variants.edges.find((v: any) => v.node.id === selectedVariantId)?.node;
    if (!selectedVariant) return;
    const cartItem = {
      product: {
        node: product
      },
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions || []
    };
    addItem(cartItem);
    toast.success("Added to cart", {
      description: `${product.title} has been added to your cart.`
    });
  };
  if (isLoading) {
    return <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>;
  }
  if (!product) {
    return <Layout>
        <div className="max-w-screen-xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">Product not found</h1>
          <Button onClick={() => navigate('/shop')} variant="outline">
            Back to Shop
          </Button>
        </div>
      </Layout>;
  }
  const productImages = product.images?.edges?.map((edge: any) => edge.node) || [];
  const price = parseFloat(product.priceRange.minVariantPrice.amount);
  const currencyCode = product.priceRange.minVariantPrice.currencyCode;

  // Get collection info for breadcrumb
  const collection = product.collections?.edges?.[0]?.node;
  const breadcrumbItems = collection ? [{
    label: 'Shop',
    href: '/shop'
  }, {
    label: collection.title,
    href: `/shop/collection/${collection.handle}`
  }] : [{
    label: 'Shop',
    href: '/shop'
  }];
  return <Layout>
      <div className="min-h-screen pb-20">
        {/* Category Navigation */}
        <ShopCategoryNav />
        
        <div className="max-w-screen-xl mx-auto px-4 pb-8 md:py-16">
          {/* Breadcrumb Navigation */}
          <div className="mb-3">
            <ShopBreadcrumb items={breadcrumbItems} />
          </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12">
          {/* Product Image Gallery */}
          <ProductImageGallery images={productImages} productTitle={product.title} />

          {/* Product Info */}
          <div className="flex flex-col">
            {collection && (
              <p className="text-xs md:text-sm text-muted-foreground uppercase tracking-wider mb-2">
                {collection.title}
              </p>
            )}
            <h1 className="md:text-4xl mb-4 font-normal text-xl">{product.title}</h1>
            
            <p className="mb-6 font-normal text-xl">
              {currencyCode} {price.toFixed(2)}
            </p>

            {product.description && <div className="mb-6">
                <p className="text-muted-foreground leading-relaxed">
                  {product.description}
                </p>
              </div>}

            {/* Variants */}
            {product.variants.edges.length > 1 && <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Variant</label>
                <select value={selectedVariantId} onChange={e => setSelectedVariantId(e.target.value)} className="w-full p-2 border rounded-md">
                  {product.variants.edges.map((variant: any) => <option key={variant.node.id} value={variant.node.id}>
                      {variant.node.title}
                    </option>)}
                </select>
              </div>}

            <Button onClick={handleAddToCart} variant="cart" size="lg" className="w-full md:w-auto">
              <ShoppingCart className="mr-2 h-5 w-5" />
              Add to Cart
            </Button>
          </div>
        </div>

        {/* Related Products Section */}
        {relatedProducts.length > 0 && <div className="mt-16 md:mt-24">
            <div className="mb-3 md:mb-6 flex items-center justify-between">
              <h2 className="md:text-3xl font-normal text-xl">
                {collection ? `More from ${collection.title}` : 'Related Products'}
              </h2>
              {collection && <Button variant="ghost" onClick={() => navigate(`/shop/collection/${collection.handle}`)} className="hidden md:flex">
                  View All
                </Button>}
            </div>

            <HorizontalScroller noLeftPadding={true} showNextCard={true} spacing="custom-2px" snap={false}>
              {relatedProducts.map(product => <div key={product.node.id} className="w-[260px] md:w-[320px] flex-shrink-0">
                  <ProductCard product={product} />
                </div>)}
            </HorizontalScroller>

            {collection && <div className="mt-4 md:hidden">
                <Button variant="black" className="w-full" onClick={() => navigate(`/shop/collection/${collection.handle}`)}>
                  View All in {collection.title}
                </Button>
              </div>}
          </div>}
        </div>
      </div>
    </Layout>;
};
export default ProductDetail;